import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Grid } from 'semantic-ui-react'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { DateTime } from 'luxon'

import {
  Button,
  Card,
  GridRowColumn,
  Icon,
  Text,
} from '../../components/BaseComponents'
import { InsightOverviewCards } from '../../components/Finances/PracticeInsights/InsightOverviewCard'
import InsightsTrendGraph from '../../components/Finances/PracticeInsights/InsightsTrendGraph'
import { fetchInsightsBarchartData } from '../../components/Finances/shared/financeActions'
import {
  getInsightsDateRange,
  INSIGHTS_TYPE,
  processDataForChart,
} from '../../components/Finances/shared/utils'
import { useAppDispatch } from '../../utils/typeHelpers'
import { useEndDateForFinancialInsights } from '../../utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '../../utils/dateHelpers'
import IncomeGoalCardV2 from './DashboardInsightsCards/IncomeGoalCardV2'
import TaxDeductionTipCard from './DashboardInsightsCards/TaxDeductionTipCard'
import { useAnalyticsTrack } from '@/featuresV1/Amplitude'
import { useBooleanFlagValue, useFlag } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'
import PracticeOverviewAlert from './PracticeOverviewAlerts/PracticeOverviewAlert'

const PracticeOverviewCard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const showLever1EarlyAccessToCentralizedWeeklyDashboardMvp =
    useBooleanFlagValue(
      FEATURE_FLAG_KEYS.enableLever1EarlyAccessToCentralizedWeeklyDashboardMvp,
      false
    )

  const currentDate = useMemo(() => DateTime.now(), [])
  const [trendData, setTrendData] = useState<
    { [key: string]: string | number | undefined }[]
  >([])

  const endDate = useEndDateForFinancialInsights(currentDate)
  const dateRange = useMemo(() => getInsightsDateRange(endDate), [endDate])
  const { value: enableTaxDeductionsDashboard } = useFlag(
    FEATURE_FLAG_KEYS.enableTaxDeductionsDashboard,
    false
  )

  useEffect(() => {
    const fetchTrendData = async () => {
      const [startDate, endDate] = dateRange
      const fetchedData = await fetchInsightsBarchartData(
        INSIGHTS_TYPE.PROFIT,
        startDate,
        endDate
      )(dispatch)

      if (fetchedData) {
        const chartData = processDataForChart(
          fetchedData,
          dateRange,
          INSIGHTS_TYPE.PROFIT
        )
        setTrendData(chartData)
      }
    }
    fetchTrendData()
  }, [dispatch, dateRange])

  const dateRangeString = useMemo(() => {
    const [startDate, endDate] = dateRange
    const start = DateTime.fromFormat(
      startDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    const end = DateTime.fromFormat(
      endDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    return `Last 12 months (${start} - ${end})`
  }, [dateRange])

  return (
    <Card type="section">
      <Grid>
        <Grid.Row verticalAlign="middle">
          <Grid.Column width={10}>
            <Text as="h2">
              {endDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)} Practice
              Overview
            </Text>
          </Grid.Column>

          <Grid.Column
            width={6}
            style={{ display: 'flex', alignItems: 'flex-end' }}
          >
            <Button
              variant="secondaryLink"
              onClick={() => {
                track('Clicked Practice Insights View Reports Button')
                navigate('/insights/overview')
              }}
            >
              View Reports
              <Icon icon={light('arrow-right')} style={{ marginLeft: 8 }} />
            </Button>
          </Grid.Column>
          {showLever1EarlyAccessToCentralizedWeeklyDashboardMvp && (
            <PracticeOverviewAlert />
          )}
        </Grid.Row>
        <GridRowColumn>
          <InsightOverviewCards year={endDate.year} month={endDate.month} />
        </GridRowColumn>
        <Grid.Row verticalAlign="middle">
          <Grid.Column width={10}>
            <Text as="h3">Practice Finances Over Time</Text>
          </Grid.Column>
          <Grid.Column width={6} textAlign="right">
            <Text as="bodySm">{dateRangeString}</Text>
          </Grid.Column>
        </Grid.Row>
        <GridRowColumn>
          <InsightsTrendGraph data={trendData} />
        </GridRowColumn>
        <Grid.Row />
        {/*The tax deduction card also has the tip card.  If flag is enabled the tip card shouldn't show here*/}
        {enableTaxDeductionsDashboard ? (
          <GridRowColumn>
            <IncomeGoalCardV2 />
          </GridRowColumn>
        ) : (
          <Grid.Row>
            <Grid.Column width={6}>
              <IncomeGoalCardV2 />
            </Grid.Column>
            <Grid.Column width={10}>
              <TaxDeductionTipCard />
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Card>
  )
}

export default PracticeOverviewCard
